import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Loader2, RotateCcw, X } from 'lucide-react';

export type QuadPoint = { x: number; y: number }; // نرمال ۰..۱ نسبت به عرض/ارتفاع تصویر

type CornerId = 0 | 1 | 2 | 3; // TL, TR, BR, BL

const DEFAULT_QUAD: QuadPoint[] = [
  { x: 0.08, y: 0.08 },
  { x: 0.92, y: 0.08 },
  { x: 0.92, y: 0.92 },
  { x: 0.08, y: 0.92 },
];

function clamp01(n: number) {
  return Math.min(1, Math.max(0, n));
}

/** برش پرسپکتیو چهارگوشه → مستطیل (سریع با مش مثلثی برای موبایل) */
export async function warpQuadToImage(
  image: HTMLImageElement,
  quad: QuadPoint[],
  maxSide = 1400
): Promise<Blob> {
  const iw = image.naturalWidth;
  const ih = image.naturalHeight;
  const corners = quad.map((p) => ({ x: p.x * iw, y: p.y * ih }));

  const wTop = Math.hypot(corners[1].x - corners[0].x, corners[1].y - corners[0].y);
  const wBot = Math.hypot(corners[2].x - corners[3].x, corners[2].y - corners[3].y);
  const hL = Math.hypot(corners[3].x - corners[0].x, corners[3].y - corners[0].y);
  const hR = Math.hypot(corners[2].x - corners[1].x, corners[2].y - corners[1].y);

  let outW = Math.max(1, Math.round(Math.max(wTop, wBot)));
  let outH = Math.max(1, Math.round(Math.max(hL, hR)));
  const scale = Math.min(1, maxSide / Math.max(outW, outH));
  outW = Math.max(1, Math.round(outW * scale));
  outH = Math.max(1, Math.round(outH * scale));

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas در دسترس نیست');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, outW, outH);

  // نگاشت شبکه از مقصد به چهارگوشهٔ منبع (افین روی مثلث‌ها — سریع‌تر از پیکسل‌به‌پیکسل)
  const grid = Math.max(8, Math.min(24, Math.round(Math.max(outW, outH) / 60)));

  const srcAt = (u: number, v: number) => {
    // دوخطی بین گوشه‌ها (تقریب پرسپکتیو با مش ریز کافی است)
    const topX = corners[0].x + (corners[1].x - corners[0].x) * u;
    const topY = corners[0].y + (corners[1].y - corners[0].y) * u;
    const botX = corners[3].x + (corners[2].x - corners[3].x) * u;
    const botY = corners[3].y + (corners[2].y - corners[3].y) * u;
    return {
      x: topX + (botX - topX) * v,
      y: topY + (botY - topY) * v,
    };
  };

  const drawTri = (
    s0: { x: number; y: number },
    s1: { x: number; y: number },
    s2: { x: number; y: number },
    d0: { x: number; y: number },
    d1: { x: number; y: number },
    d2: { x: number; y: number }
  ) => {
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(d0.x, d0.y);
    ctx.lineTo(d1.x, d1.y);
    ctx.lineTo(d2.x, d2.y);
    ctx.closePath();
    ctx.clip();

    // حل تبدیل افین: مقصد → منبع
    const den =
      d0.x * (d1.y - d2.y) + d1.x * (d2.y - d0.y) + d2.x * (d0.y - d1.y);
    if (Math.abs(den) < 1e-6) {
      ctx.restore();
      return;
    }
    const m11 =
      (s0.x * (d1.y - d2.y) + s1.x * (d2.y - d0.y) + s2.x * (d0.y - d1.y)) / den;
    const m12 =
      (s0.x * (d2.x - d1.x) + s1.x * (d0.x - d2.x) + s2.x * (d1.x - d0.x)) / den;
    const m13 =
      (s0.x * (d1.x * d2.y - d2.x * d1.y) +
        s1.x * (d2.x * d0.y - d0.x * d2.y) +
        s2.x * (d0.x * d1.y - d1.x * d0.y)) /
      den;
    const m21 =
      (s0.y * (d1.y - d2.y) + s1.y * (d2.y - d0.y) + s2.y * (d0.y - d1.y)) / den;
    const m22 =
      (s0.y * (d2.x - d1.x) + s1.y * (d0.x - d2.x) + s2.y * (d1.x - d0.x)) / den;
    const m23 =
      (s0.y * (d1.x * d2.y - d2.x * d1.y) +
        s1.y * (d2.x * d0.y - d0.x * d2.y) +
        s2.y * (d0.x * d1.y - d1.x * d0.y)) /
      den;

    ctx.setTransform(m11, m21, m12, m22, m13, m23);
    ctx.drawImage(image, 0, 0);
    ctx.restore();
  };

  for (let gy = 0; gy < grid; gy++) {
    for (let gx = 0; gx < grid; gx++) {
      const u0 = gx / grid;
      const v0 = gy / grid;
      const u1 = (gx + 1) / grid;
      const v1 = (gy + 1) / grid;

      const d00 = { x: u0 * outW, y: v0 * outH };
      const d10 = { x: u1 * outW, y: v0 * outH };
      const d01 = { x: u0 * outW, y: v1 * outH };
      const d11 = { x: u1 * outW, y: v1 * outH };

      const s00 = srcAt(u0, v0);
      const s10 = srcAt(u1, v0);
      const s01 = srcAt(u0, v1);
      const s11 = srcAt(u1, v1);

      drawTri(s00, s10, s11, d00, d10, d11);
      drawTri(s00, s11, s01, d00, d11, d01);
    }
  }

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.9)
  );
  if (!blob) throw new Error('خروجی کراپ ساخته نشد');
  return blob;
}

interface ReceiptCropModalProps {
  show: boolean;
  /** object URL یا data URL تصویر خام */
  imageUrl: string | null;
  onClose: () => void;
  onConfirm: (file: File) => void;
  onSkip?: () => void;
}

const ReceiptCropModal: React.FC<ReceiptCropModalProps> = ({
  show,
  imageUrl,
  onClose,
  onConfirm,
  onSkip,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [quad, setQuad] = useState<QuadPoint[]>(DEFAULT_QUAD.map((p) => ({ ...p })));
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [box, setBox] = useState({ left: 0, top: 0, width: 0, height: 0 });
  const [dragging, setDragging] = useState<CornerId | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);

  const measure = useCallback(() => {
    const img = imgRef.current;
    const wrap = containerRef.current;
    if (!img || !wrap || !img.naturalWidth) return;
    const rect = img.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    setBox({
      left: rect.left - wrapRect.left,
      top: rect.top - wrapRect.top,
      width: rect.width,
      height: rect.height,
    });
    setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
  }, []);

  useEffect(() => {
    if (!show) return;
    setQuad(DEFAULT_QUAD.map((p) => ({ ...p })));
    setError('');
    setBusy(false);
    setLoaded(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [show, imageUrl]);

  useEffect(() => {
    if (!show || !loaded) return;
    measure();
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [show, loaded, measure]);

  const clientToNorm = (clientX: number, clientY: number): QuadPoint => {
    const wrap = containerRef.current;
    if (!wrap || box.width <= 0) return { x: 0.5, y: 0.5 };
    const wrapRect = wrap.getBoundingClientRect();
    const x = (clientX - wrapRect.left - box.left) / box.width;
    const y = (clientY - wrapRect.top - box.top) / box.height;
    return { x: clamp01(x), y: clamp01(y) };
  };

  useEffect(() => {
    if (dragging == null) return;

    const move = (clientX: number, clientY: number) => {
      const p = clientToNorm(clientX, clientY);
      setQuad((prev) => {
        const next = prev.map((c) => ({ ...c }));
        next[dragging] = p;
        return next;
      });
    };

    const onPointerMove = (e: PointerEvent) => {
      e.preventDefault();
      move(e.clientX, e.clientY);
    };
    const onPointerUp = () => setDragging(null);

    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clientToNorm از box می‌خواند
  }, [dragging, box]);

  if (!show || !imageUrl) return null;

  const toDisplay = (p: QuadPoint) => ({
    x: box.left + p.x * box.width,
    y: box.top + p.y * box.height,
  });

  const pts = quad.map(toDisplay);
  const polygon = pts.map((p) => `${p.x},${p.y}`).join(' ');

  const handleConfirm = async () => {
    const img = imgRef.current;
    if (!img || !img.complete) return;
    setBusy(true);
    setError('');
    try {
      // روی موبایل کمی صبر برای نقاشی UI
      await new Promise((r) => setTimeout(r, 30));
      const blob = await warpQuadToImage(img, quad);
      const file = new File([blob], `receipt-crop-${Date.now()}.jpg`, { type: 'image/jpeg' });
      onConfirm(file);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'خطا در کراپ');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[145] flex flex-col bg-black/85"
      dir="rtl"
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2.5 bg-black/40 text-white shrink-0">
        <div>
          <h3 className="font-bold text-sm sm:text-base">تنظیم چهار گوشه فیش</h3>
          <p className="text-[11px] text-white/70 mt-0.5">گوشه‌ها را با انگشت جابه‌جا کنید</p>
        </div>
        <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-white/10" aria-label="بستن">
          <X size={20} />
        </button>
      </div>

      <div
        ref={containerRef}
        className="relative flex-1 min-h-0 flex items-center justify-center p-3 touch-none select-none overflow-hidden"
      >
        <img
          ref={imgRef}
          src={imageUrl}
          alt="فیش"
          draggable={false}
          className="max-h-full max-w-full object-contain pointer-events-none"
          onLoad={() => {
            setLoaded(true);
            requestAnimationFrame(measure);
          }}
        />

        {loaded && box.width > 0 && (
          <svg
            className="absolute inset-0 pointer-events-none"
            width="100%"
            height="100%"
            style={{ touchAction: 'none' }}
          >
            <defs>
              <mask id="receipt-crop-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                <polygon points={polygon} fill="black" />
              </mask>
            </defs>
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="rgba(0,0,0,0.45)"
              mask="url(#receipt-crop-mask)"
            />
            <polygon
              points={polygon}
              fill="rgba(14,165,233,0.15)"
              stroke="#38bdf8"
              strokeWidth={2.5}
              strokeLinejoin="round"
            />
            {pts.map((p, i) => (
              <g key={i}>
                {/* هیت‌اریا بزرگ برای انگشت */}
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={28}
                  fill="rgba(14,165,233,0.2)"
                  className="pointer-events-auto"
                  style={{ touchAction: 'none', cursor: 'grab' }}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    try {
                      (e.target as Element).setPointerCapture?.(e.pointerId);
                    } catch {
                      /* ignore */
                    }
                    setDragging(i as CornerId);
                  }}
                />
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={16}
                  fill="#0ea5e9"
                  stroke="white"
                  strokeWidth={3}
                  className="pointer-events-none"
                />
              </g>
            ))}
          </svg>
        )}

        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center text-white">
            <Loader2 className="animate-spin" size={28} />
          </div>
        )}
      </div>

      {error && (
        <p className="px-4 py-2 text-sm text-rose-200 bg-rose-950/50 shrink-0">{error}</p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-3 bg-black/50 shrink-0">
        <button
          type="button"
          onClick={() => setQuad(DEFAULT_QUAD.map((p) => ({ ...p })))}
          className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm text-white/90 hover:bg-white/10"
        >
          <RotateCcw size={16} />
          بازنشانی
        </button>
        <div className="flex gap-2 flex-1 justify-end">
          {onSkip && (
            <button
              type="button"
              onClick={() => {
                onSkip();
                onClose();
              }}
              className="px-3 py-2.5 rounded-lg text-sm text-white/80 hover:bg-white/10"
            >
              بدون کراپ
            </button>
          )}
          <button
            type="button"
            disabled={busy || !loaded || imgSize.w === 0}
            onClick={() => void handleConfirm()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-sky-500 text-white font-medium hover:bg-sky-600 disabled:opacity-50"
          >
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
            تأیید کراپ
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptCropModal;
